import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PASSWORD_MIN, passwordProblem } from '@shared/constants';
import type { Me, UserEmail } from '@shared/types';
import { ApiError, api, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/hooks';
import { openDialog } from '@/lib/overlays';
import { Slot } from '@/lib/slots';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import { Avatar } from '@/ui/Avatar';
import { Callout, Chip } from '@/ui/bits';
import { DialogBody, DialogDone, DialogFoot, DialogHead } from '@/ui/Dialog';
import { InputField } from '@/ui/Field';
import { GoogleMark, Icon } from '@/ui/Icon';
import { MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';

// Your own account: your name, the email addresses that are yours (work and private) and whether each signs in
// with Google, your Gmail connection, your Google sign-in and your password.

/** Opens your account settings, optionally starting at the password. */
export function openAccount(part?: 'password'): void {
  openDialog({
    label: 'Your account',
    size: 'mid',
    render: (close) => <AccountDialog close={close} focusPassword={part === 'password'} />,
  });
}

/** Connecting Google from here links the Google account and confirms its address; `address` suggests which account. */
function googleConnectUrl(address?: string): string {
  return `/auth/google/start?purpose=account${address ? `&address=${encodeURIComponent(address)}` : ''}`;
}

/** How an address takes part in Google sign-in, or null when that does not apply. */
type GoogleStatus = 'linked' | 'confirmed' | 'unconfirmed';

function googleStatus(me: Me, entry: UserEmail): GoogleStatus | null {
  if (me.user.googleLinked) return me.user.googleEmail?.toLowerCase() === entry.email.toLowerCase() ? 'linked' : null;
  if (!me.integrations.google) return null;
  return entry.confirmed ? 'confirmed' : 'unconfirmed';
}

function refreshMe(): Promise<void> {
  return Promise.all([queryClient.invalidateQueries({ queryKey: ['me'] }), queryClient.invalidateQueries({ queryKey: ['team'] })]).then(() => undefined);
}

function AccountDialog({ close, focusPassword }: { close: () => void; focusPassword: boolean }) {
  const { data: me } = useQuery(meQuery);
  if (!me) return null;
  return (
    <>
      <DialogHead title="Your account" onClose={close} />
      <DialogBody className="acct">
        <NameSection me={me} />
        <EmailSection me={me} />
        <Slot name="account.inbox" />
        <GoogleSection me={me} />
        <PasswordSection me={me} autoFocus={focusPassword} />
      </DialogBody>
      <DialogFoot>
        <span className="note">
          <Icon name="shield" />
          {me.user.role === 'super_admin' ? 'Super admin' : 'Member'} of {me.workspace.name}
        </span>
        <span className="spacer" />
        <DialogDone onClick={close} autoFocus={!focusPassword} />
      </DialogFoot>
    </>
  );
}

function NameSection({ me }: { me: Me }) {
  const [name, setName] = useState(me.user.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changed = name.trim() !== me.user.name && name.trim().length > 0;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      setError('Enter your name');
      return;
    }
    if (!changed) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch('/me', { name: name.trim() });
      await refreshMe();
      toast('Name saved', { icon: 'check-circle' });
    } catch (err) {
      setError(err instanceof ApiError && err.fields.name ? err.fields.name : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dlg-sec" aria-labelledby="acct-name-h">
      <h3 className="flabel" id="acct-name-h">
        Profile
      </h3>
      <form className="acct-me" onSubmit={save} noValidate>
        <Avatar name={name.trim() || me.user.name} hue={me.user.hue} size="lg" />
        <InputField label="Name" autoComplete="name" value={name} maxLength={80} error={error} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="btn btn-tile" disabled={!changed || busy} aria-busy={busy || undefined}>
          {busy ? 'Saving' : 'Save'}
        </button>
      </form>
    </section>
  );
}

function EmailSection({ me }: { me: Me }) {
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<UserEmail['kind']>('private');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emails = [...me.user.emails].sort((a, b) => Number(b.primary) - Number(a.primary) || a.email.localeCompare(b.email));

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(value ? 'Enter a full email address, such as name@company.com' : 'Enter the address you want to add.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/me/emails', { email: value, kind });
      await refreshMe();
      setEmail('');
      toast(`${value} added to your addresses`, { icon: 'check-circle' });
    } catch (err) {
      setError(err instanceof ApiError && err.fields.email ? err.fields.email : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dlg-sec" aria-labelledby="acct-emails-h">
      <h3 className="flabel" id="acct-emails-h">
        Your email addresses
      </h3>
      <ul className="acct-emails">
        {emails.map((e) => (
          <EmailRow key={e.email} entry={e} only={emails.length === 1} google={googleStatus(me, e)} hasPassword={me.user.hasPassword} />
        ))}
      </ul>
      <form className="acct-add" onSubmit={add} noValidate>
        <input
          className="input"
          type="email"
          placeholder="Add another address you email from"
          aria-label="Email address to add"
          aria-invalid={error ? true : undefined}
          aria-describedby="acct-add-help"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
        />
        <select className="select" aria-label="Kind of address" value={kind} onChange={(e) => setKind(e.target.value as UserEmail['kind'])}>
          <option value="private">Private</option>
          <option value="work">Work</option>
        </select>
        <button type="submit" className="btn btn-tile" disabled={busy} aria-busy={busy || undefined}>
          <Icon name="plus" />
          Add
        </button>
      </form>
      {error ? (
        <p className="field-err acct-err" role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      <p className="field-help acct-help" id="acct-add-help">
        {me.workspace.crmAddress ? (
          <>
            Emails you forward to <b>{me.workspace.crmAddress}</b> are accepted from any of these addresses.{' '}
          </>
        ) : null}
        Emails between teammates&apos; addresses stay out of client records.
        {me.integrations.google && !me.user.googleLinked ? ' You can sign in with Google through a confirmed address.' : null}
      </p>
    </section>
  );
}

function EmailRow({ entry, only, google, hasPassword }: { entry: UserEmail; only: boolean; google: GoogleStatus | null; hasPassword: boolean }) {
  const menu = usePopover('menu');
  const [busy, setBusy] = useState(false);

  const run = async <T,>(fn: () => Promise<T>, done: string | ((result: T) => string)) => {
    setBusy(true);
    try {
      const result = await fn();
      await refreshMe();
      toast(typeof done === 'string' ? done : done(result), { icon: 'check-circle' });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const other: UserEmail['kind'] = entry.kind === 'work' ? 'private' : 'work';
  const unlinks = google === 'linked';
  return (
    <li className="acct-email" aria-busy={busy || undefined}>
      <Icon name={entry.kind === 'work' ? 'briefcase' : 'user'} className="i-sm" />
      <div className="grow">
        <span className="acct-email-addr" title={entry.email}>
          {entry.email}
        </span>
        {google === 'unconfirmed' ? (
          <span className="acct-email-g">
            Not confirmed for Google sign-in ·{' '}
            <a className="link" href={googleConnectUrl(entry.email)}>
              Confirm with Google
            </a>
          </span>
        ) : google ? (
          <span className="acct-email-g ok">
            <Icon name="check" className="i-xs" />
            {google === 'linked' ? 'Linked for Google sign-in' : 'Confirmed for Google sign-in'}
          </span>
        ) : null}
      </div>
      {entry.primary ? (
        <Chip tone="accent" title="Shown to the team as your address">
          Main
        </Chip>
      ) : null}
      <Chip>{entry.kind === 'work' ? 'Work' : 'Private'}</Chip>
      <button type="button" className="icon-btn xs" aria-label={`Options for ${entry.email}`} data-tip="Options" {...menu.anchorProps}>
        <Icon name="more" />
      </button>
      <Popover state={menu} width={230} label={`Options for ${entry.email}`}>
        <MenuItem
          icon="check-circle"
          disabled={entry.primary}
          onSelect={() => void run(() => api.patch('/me/emails', { email: entry.email, primary: true }), `${entry.email} is now your main address`)}
        >
          Make it your main address
        </MenuItem>
        <MenuItem
          icon={other === 'work' ? 'briefcase' : 'user'}
          onSelect={() => void run(() => api.patch('/me/emails', { email: entry.email, kind: other }), `${entry.email} marked as ${other}`)}
        >
          Mark as {other}
        </MenuItem>
        <MenuSep />
        <MenuItem
          icon="trash"
          danger
          disabled={entry.primary || only || (unlinks && !hasPassword)}
          sub={entry.primary ? 'Main address' : unlinks ? (hasPassword ? 'Also unlinks Google sign-in' : 'Add a password first') : undefined}
          onSelect={() =>
            void run(
              () => api.del<{ unlinked: boolean }>('/me/emails', { email: entry.email }),
              (r) => (r.unlinked ? `${entry.email} removed. Google sign-in is unlinked` : `${entry.email} removed`),
            )
          }
        >
          Remove
        </MenuItem>
      </Popover>
    </li>
  );
}

function GoogleSection({ me }: { me: Me }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { googleLinked, googleEmail, hasPassword } = me.user;
  const configured = me.integrations.google;
  if (!googleLinked && !configured) return null;

  const unlink = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.del('/auth/google');
      await refreshMe();
      toast('Google sign-in unlinked', {
        icon: 'logout',
        action: configured ? { label: 'Connect again', run: () => window.location.assign(googleConnectUrl()) } : undefined,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dlg-sec" aria-labelledby="acct-google-h">
      <h3 className="flabel" id="acct-google-h">
        Google sign-in
      </h3>
      <div className="dest">
        <span className="svc ggl" aria-hidden="true">
          <GoogleMark />
        </span>
        {googleLinked ? (
          <>
            <div className="grow">
              <b>{googleEmail ?? 'Your linked Google account'}</b>
              <span>Only this Google account signs you in with Google.</span>
            </div>
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy || !hasPassword} aria-busy={busy || undefined} onClick={() => void unlink()}>
              <Icon name="logout" />
              {busy ? 'Unlinking' : 'Unlink'}
            </button>
          </>
        ) : (
          <>
            <div className="grow">
              <b>Connect your Google account</b>
              <span>Its address is confirmed for Google sign-in, and from then on only that account signs you in with Google.</span>
            </div>
            <a className="btn btn-sm btn-primary" href={googleConnectUrl()}>
              <Icon name="link" />
              Connect Google
            </a>
          </>
        )}
      </div>
      {googleLinked && !hasPassword ? <p className="field-help acct-help">Add a password first, so you can still sign in after unlinking Google.</p> : null}
      {error ? (
        <p className="field-err acct-err" role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
    </section>
  );
}

function PasswordSection({ me, autoFocus }: { me: Me; autoFocus: boolean }) {
  const hasPassword = me.user.hasPassword;
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const local: Record<string, string> = {};
    if (hasPassword && !current) local.current = 'Enter your current password';
    const rule = passwordProblem(next);
    if (rule) local.next = rule;
    else if (again !== next) local.again = 'The two passwords are different.';
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.post('/auth/password', hasPassword ? { current, next } : { next });
      setCurrent('');
      setNext('');
      setAgain('');
      await refreshMe();
      toast(hasPassword ? 'Password changed. Your other devices are signed out' : 'Password added. You can sign in with it now', { icon: 'lock' });
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setFields(err.fields);
      else setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dlg-sec" aria-labelledby="acct-pw-h">
      <h3 className="flabel" id="acct-pw-h">
        {hasPassword ? 'Password' : 'Add a password'}
      </h3>
      {me.user.passwordIsSample ? (
        <Callout tone="warn" title="You are using the sample password from setup" className="acct-callout">
          Anyone who has read the setup notes can sign in as you. Choose your own password below.
        </Callout>
      ) : !hasPassword ? (
        <p className="field-help acct-help top">You sign in with Google. A password lets you sign in with your email address too.</p>
      ) : null}
      {problem ? (
        <Callout tone="danger" role="alert" className="acct-callout">
          {problem}
        </Callout>
      ) : null}
      <form className="acct-pw" onSubmit={save} noValidate>
        <input type="email" name="username" autoComplete="username" value={me.user.email ?? ''} readOnly hidden />
        {hasPassword ? (
          <InputField
            className="wide"
            label="Current password"
            type="password"
            autoComplete="current-password"
            autoFocus={autoFocus}
            data-autofocus={autoFocus ? true : undefined}
            value={current}
            error={fields.current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        ) : null}
        <InputField
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus={autoFocus && !hasPassword}
          data-autofocus={autoFocus && !hasPassword ? true : undefined}
          help={`At least ${PASSWORD_MIN} characters. A short sentence works well.`}
          value={next}
          error={fields.next}
          onChange={(e) => setNext(e.target.value)}
        />
        <InputField label="Type it again" type="password" autoComplete="new-password" value={again} error={fields.again} onChange={(e) => setAgain(e.target.value)} />
        <div className="wide acct-pw-foot">
          {hasPassword ? <span className="field-help">Changing it signs you out on your other devices.</span> : <span />}
          <button type="submit" className="btn btn-tile" disabled={busy} aria-busy={busy || undefined}>
            <Icon name="lock" />
            {busy ? 'Saving' : hasPassword ? 'Change password' : 'Add password'}
          </button>
        </div>
      </form>
    </section>
  );
}
